import { Coordinate, ChargeEvent, FillUp, FuelTransaction, FuelUsed } from "@fairfleet/geotab";
import groupBy from "lodash/groupBy";
import { AuditDevice } from "../models/AuditDevice";
import { AuditTrip, DetectedEvent, RefuelEntry } from "../models/AuditTrip";
import { analogDurationMs, buildAnalogJourneys, readingKm } from "./analogJourneys";
import { assignAnnotations } from "./annotationForTrip";
import { detectedEventsForTrip, refuelledAmount } from "./detectedEvents";
import { isPhantomTrip } from "./isPhantomTrip";
import { odometerDistances } from "./odometerDistance";
import { VehicleDay } from "./periodSummary";
import { classifyReason, ReasonTaxClassMap, splitManualSuffix } from "./reasonTaxClass";
import { dayKeyInZone, parseDayKey, startOfCalendarDayInZone } from "./timeZone";
import {
  electricEnergyForWindow,
  fuelForWindow,
  measuredConsumption,
  nextStartAfter,
  refuelLiters,
  refuelsForTrip,
  refuelsInWindow,
} from "./tripConsumption";
import { tripDriverId } from "./tripDriver";
import { DutyRosterRecord, TripEndRecord } from "@/hooks/addins/useManualLogbookDutyRosters";
import { ElectricEnergyReadings } from "@/hooks/statusData/useElectricEnergyForDevices";
import { DeviceTrips } from "@/hooks/trips/useTripsForDevices";

export interface AuditEntriesInput {
  fromDate: Date;
  toDate: Date;
  /** Zone in which calendar days are cut (the Geotab user's zone). */
  timeZone: string;
  auditDevices: Map<string, AuditDevice>;
  taxClasses: ReasonTaxClassMap;
  tripsByDevice: Record<string, DeviceTrips>;
  dutyRosters: DutyRosterRecord[];
  tripEnds: TripEndRecord[];
  fuelUsedByDevice: Record<string, FuelUsed[]>;
  fuelTransactionsByDevice: Record<string, FuelTransaction[]>;
  electricEnergyByDevice: Record<string, ElectricEnergyReadings>;
  fillUpsByDevice: Record<string, FillUp[]>;
  chargeEventsByDevice: Record<string, ChargeEvent[]>;
}

export interface AuditEntries {
  auditTrips: AuditTrip[];
  /** Kilometres of every Geotab trip per vehicle and calendar day of the period, booked or not. */
  vehicleDays: VehicleDay[];
  periodRefuels: Map<string, RefuelEntry[]>;
  periodDetected: Map<string, DetectedEvent[]>;
}

const inPeriod = (time: Date, fromDate: Date, toDate: Date) => time >= fromDate && time <= toDate;

/** Everything one vehicle contributes to the audit. */
interface DeviceEntries {
  trips: AuditTrip[];
  days: VehicleDay[];
  refuels: RefuelEntry[];
  detected: DetectedEvent[];
}

interface DeviceInputs {
  deviceTrips: DeviceTrips;
  device: AuditDevice;
  rosters: DutyRosterRecord[];
  tripEnds: TripEndRecord[];
  transactions: FuelTransaction[] | undefined;
  fuelUsed: FuelUsed[] | undefined;
  electricEnergy: ElectricEnergyReadings | undefined;
  fillUps: FillUp[] | undefined;
  chargeEvents: ChargeEvent[] | undefined;
}

interface PeriodContext {
  fromDate: Date;
  toDate: Date;
  timeZone: string;
  taxClasses: ReasonTaxClassMap;
}

function buildDeviceEntries(
  { deviceTrips, device, rosters, tripEnds, transactions, fuelUsed, electricEnergy, fillUps, chargeEvents }: DeviceInputs,
  { fromDate, toDate, timeZone, taxClasses }: PeriodContext,
): DeviceEntries {
  const trips: AuditTrip[] = [];
  const days: VehicleDay[] = [];
  const period = { start: fromDate, stop: toDate };
  let refuelsInPeriod: RefuelEntry[] = [];
  let detectedInPeriod: DetectedEvent[] = [];
  // The query also returns the week before the period (odometer predecessors) and trips that
  // merely overlap it; the audit counts an entry on the day it starts.
  const realTrips = deviceTrips.trips
    .filter(trip => !isPhantomTrip(trip))
    .sort((a, b) => a.start.getTime() - b.start.getTime());
  const distances = odometerDistances(realTrips, toDate);
  const annotationsByTrip = assignAnnotations(realTrips, deviceTrips.annotations);
    const reasonOf = (tripId: string) => splitManualSuffix(annotationsByTrip.get(tripId)?.text).reason;
  refuelsInPeriod = refuelsInWindow(period, transactions);
  detectedInPeriod = detectedEventsForTrip(period, fillUps, chargeEvents, undefined, toDate);

  // Odometer kilometres of every Geotab trip per calendar day, booked or not, for the period
  // summary. Phantom trips add a day with 0 km: Geotab still books FuelUsed to them (vehicle AB-CD 103
  // of a customer: 52 l on an ignition blip at the depot, 09.08.2026), and the summary must
  // add up to Geotab's fuel report.
  const dayKm = new Map<string, number>();
  for (const trip of deviceTrips.trips) {
    const km = isPhantomTrip(trip) ? 0 : (distances.get(trip.id)?.distance ?? 0);
    const dateKey = dayKeyInZone(trip.start, timeZone);
    dayKm.set(dateKey, (dayKm.get(dateKey) ?? 0) + Math.max(0, km));
  }
  for (const [dateKey, km] of dayKm) {
    const { year, month, day } = parseDayKey(dateKey);
    if (!inPeriod(startOfCalendarDayInZone(year, month, day, timeZone), fromDate, toDate)) continue;
    days.push({ deviceId: device.id, dateKey, km });
  }

  const journeys = buildAnalogJourneys(
    realTrips,
    rosters,
    tripEnds,
  );
  const covered = new Set(journeys.flatMap(journey => journey.trips.map(trip => trip.id)));
  const entryStarts = [
    ...journeys.map(journey => journey.roster.recordedAt.getTime()),
    ...realTrips.filter(trip => !covered.has(trip.id)).map(trip => trip.start.getTime()),
  ].sort((a, b) => a - b);

  for (const journey of journeys) {
    const { roster, tripEnd, trips: journeyTrips, candidates } = journey;
    if (!inPeriod(roster.recordedAt, fromDate, toDate)) continue;
    const duration = analogDurationMs(journey);
    const reasonTrip = journeyTrips.find(trip => reasonOf(trip.id));
    const reason = reasonTrip ? reasonOf(reasonTrip.id) : undefined;
    const lastTrip = journeyTrips[journeyTrips.length - 1];
    // Only for finding the driver's fuel entries; the window itself is not shown.
    const refuelWindow = { start: roster.recordedAt, stop: tripEnd?.recordedAt ?? lastTrip?.stop ?? roster.recordedAt };
    const refuels = refuelsForTrip(refuelWindow, transactions, nextStartAfter(entryStarts, roster.recordedAt)).filter(entry => entry.source === "app");
    const odometerStartKm = readingKm(roster);
    const odometerEndKm = readingKm(tripEnd);
    const distance = odometerStartKm != null && odometerEndKm != null ? odometerEndKm - odometerStartKm : undefined;
    trips.push({
      id: roster.id,
      device,
      driverId: roster.userId,
      recordType: "analog",
      start: roster.recordedAt,
      stop: tripEnd?.recordedAt,
      durationMs: duration?.durationMs,
      durationFromGeotabTrips: duration?.fromGeotabTrips,
      distance,
      odometerStartKm,
      odometerEndKm,
      rawReason: reasonTrip ? annotationsByTrip.get(reasonTrip.id)?.text : undefined,
      reason,
      taxClass: classifyReason(reason, taxClasses),
      dutyRoster: roster,
      tripEnd,
      dutyRosterCandidates: candidates,
      refuels,
      refuelLiters: refuelLiters(refuels, "fuel"),
      adBlueLiters: refuelLiters(refuels, "adBlue"),
      detectedEvents: [],
    });
  }

  realTrips.forEach(trip => {
    if (covered.has(trip.id) || !inPeriod(trip.start, fromDate, toDate)) return;
    const driverId = tripDriverId(trip);
    const nextTripStart = nextStartAfter(entryStarts, trip.start);
    // An unbooked trip has no driver and lends no reason, whatever its annotation says.
    const reason = driverId ? reasonOf(trip.id) : undefined;
    const refuels = refuelsForTrip(trip, transactions, nextTripStart);
    const detectedEvents = detectedEventsForTrip(trip, fillUps, chargeEvents, nextTripStart, toDate);
    const refuelled = refuelledAmount(refuelLiters(refuels, "fuel"), detectedEvents);
    const measured =
      device.fuelType === "electric"
        ? electricEnergyForWindow(trip, electricEnergy)
        : device.fuelType
          ? fuelForWindow(trip, fuelUsed)
          : undefined;
    const { odometerStartKm, odometerEndKm, distance, odometerJump, odometerGap } = distances.get(trip.id) ?? {};
    const consumption = measuredConsumption(measured, distance);
    trips.push({
      id: trip.id,
      device,
      driverId: driverId || undefined,
      recordType: "digital",
      start: trip.start,
      stop: trip.stop,
      durationMs: Math.max(0, trip.stop.getTime() - trip.start.getTime()),
      distance,
      odometerJump,
      odometerGap,
      odometerStartKm,
      odometerEndKm,
      stopPoint: trip.stopPoint as Coordinate | undefined,
      rawReason: driverId ? annotationsByTrip.get(trip.id)?.text : undefined,
      reason,
      taxClass: classifyReason(reason, taxClasses),
      dutyRosterCandidates: 0,
      consumption,
      refuels,
      refuelLiters: refuelled.amount,
      refuelFromDetected: refuelled.fromDetected,
      adBlueLiters: refuelLiters(refuels, "adBlue"),
      detectedEvents,
    });
  });

  return { trips, days, refuels: refuelsInPeriod, detected: detectedInPeriod };
}

const byDevice = <T extends { deviceId: string }>(records: T[]) => groupBy(records, record => record.deviceId);

/**
 * Turns the loaded Geotab data into logbook entries: analog journeys from the drivers' duty
 * roster records, digital trips from Geotab trips (booked ones with driver and reason), plus the
 * per-vehicle day kilometres, refuels and detected events the period summary needs.
 *
 * The builder is incremental: a vehicle whose inputs are the same objects as in the previous call
 * is not computed again. The data arrives vehicle by vehicle, and rebuilding the whole fleet for
 * every arriving vehicle made loading quadratic.
 */
export function createAuditEntriesBuilder() {
  let periodKey: PeriodContext | undefined;
  let cache = new Map<string, { inputs: DeviceInputs; entries: DeviceEntries }>();
  let rostersSource: DutyRosterRecord[] | undefined;
  let rostersByDevice: Record<string, DutyRosterRecord[]> = {};
  let tripEndsSource: TripEndRecord[] | undefined;
  let tripEndsByDevice: Record<string, TripEndRecord[]> = {};

  return function build(input: AuditEntriesInput): AuditEntries {
    const context: PeriodContext = {
      fromDate: input.fromDate,
      toDate: input.toDate,
      timeZone: input.timeZone,
      taxClasses: input.taxClasses,
    };
    const sameContext =
      periodKey !== undefined &&
      periodKey.fromDate.getTime() === context.fromDate.getTime() &&
      periodKey.toDate.getTime() === context.toDate.getTime() &&
      periodKey.timeZone === context.timeZone &&
      periodKey.taxClasses === context.taxClasses;
    if (!sameContext) cache = new Map();
    periodKey = context;

    // Rosters are fleet-wide; index them once per change instead of filtering per vehicle.
    if (input.dutyRosters !== rostersSource) {
      rostersSource = input.dutyRosters;
      rostersByDevice = byDevice(input.dutyRosters);
    }
    if (input.tripEnds !== tripEndsSource) {
      tripEndsSource = input.tripEnds;
      tripEndsByDevice = byDevice(input.tripEnds);
    }

    const auditTrips: AuditTrip[] = [];
    const vehicleDays: VehicleDay[] = [];
    const periodRefuels = new Map<string, RefuelEntry[]>();
    const periodDetected = new Map<string, DetectedEvent[]>();
    const seen = new Set<string>();

    for (const deviceTrips of Object.values(input.tripsByDevice)) {
      const device = input.auditDevices.get(deviceTrips.deviceId);
      if (!device) continue;
      seen.add(device.id);
      const inputs: DeviceInputs = {
        deviceTrips,
        device,
        rosters: rostersByDevice[device.id] ?? EMPTY,
        tripEnds: tripEndsByDevice[device.id] ?? EMPTY,
        transactions: input.fuelTransactionsByDevice[device.id],
        fuelUsed: input.fuelUsedByDevice[device.id],
        electricEnergy: input.electricEnergyByDevice[device.id],
        fillUps: input.fillUpsByDevice[device.id],
        chargeEvents: input.chargeEventsByDevice[device.id],
      };
      let cached = cache.get(device.id);
      if (!cached || !sameInputs(cached.inputs, inputs)) {
        cached = { inputs, entries: buildDeviceEntries(inputs, context) };
        cache.set(device.id, cached);
      }
      const { trips, days, refuels, detected } = cached.entries;
      // A loop, not push(...): a vehicle can hold more entries than a call may take as arguments.
      for (const trip of trips) auditTrips.push(trip);
      for (const day of days) vehicleDays.push(day);
      periodRefuels.set(device.id, refuels);
      periodDetected.set(device.id, detected);
    }
    for (const id of cache.keys()) if (!seen.has(id)) cache.delete(id);

    return { auditTrips, vehicleDays, periodRefuels, periodDetected };
  };
}

const EMPTY: never[] = [];

function sameInputs(a: DeviceInputs, b: DeviceInputs): boolean {
  return (Object.keys(a) as (keyof DeviceInputs)[]).every(key => a[key] === b[key]);
}

/** One-shot build of all vehicles. */
export function buildAuditEntries(input: AuditEntriesInput): AuditEntries {
  return createAuditEntriesBuilder()(input);
}
